import type { PresentationPerformanceSample } from "../shared/clientPerformance.js";

export interface FixedRateSnapshotRendererOptions<T> {
  frameIntervalMs: number;
  onRender: (snapshot: T, nowMs: number) => void;
  requestFrame: (callback: FrameRequestCallback) => number;
  cancelFrame: (requestId: number) => void;
  now?: () => number;
  onPerformanceSample?: (sample: PresentationPerformanceSample) => void;
}

export function interpolateSnapshotPosition(
  from: number,
  to: number,
  startedAtMs: number,
  endsAtMs: number,
  nowMs: number,
): number {
  if (endsAtMs <= startedAtMs) return to;
  const progress = Math.max(0, Math.min(1, (nowMs - startedAtMs) / (endsAtMs - startedAtMs)));
  return from + (to - from) * progress;
}

/**
 * Keeps presentation cadence independent from authoritative snapshot cadence.
 *
 * New snapshots replace older pending state, while the latest state continues to render on fixed
 * deadlines for local animation. Deadlines advance from their prior target rather than the actual
 * callback time so 50 Hz and 60 Hz displays average the requested cadence instead of drifting.
 */
export class FixedRateSnapshotRenderer<T> {
  private readonly frameIntervalMs: number;
  private readonly onRender: (snapshot: T, nowMs: number) => void;
  private readonly requestFrame: (callback: FrameRequestCallback) => number;
  private readonly cancelFrame: (requestId: number) => void;
  private readonly now: () => number;
  private readonly onPerformanceSample:
    | ((sample: PresentationPerformanceSample) => void)
    | undefined;
  private latestSnapshot: T | null = null;
  private latestSnapshotSubmittedAt = 0;
  private frameRequest: number | null = null;
  private nextDrawAt = 0;
  private running = false;

  constructor(options: FixedRateSnapshotRendererOptions<T>) {
    if (!Number.isFinite(options.frameIntervalMs) || options.frameIntervalMs <= 0) {
      throw new Error("frameIntervalMs must be a positive finite number.");
    }
    this.frameIntervalMs = options.frameIntervalMs;
    this.onRender = options.onRender;
    this.requestFrame = options.requestFrame;
    this.cancelFrame = options.cancelFrame;
    this.now = options.now ?? ((): number => performance.now());
    this.onPerformanceSample = options.onPerformanceSample;
  }

  submit(snapshot: T): void {
    this.latestSnapshot = snapshot;
    this.latestSnapshotSubmittedAt = this.now();
    if (this.running) return;
    this.running = true;
    this.nextDrawAt = 0;
    this.schedule();
  }

  stop(): void {
    this.running = false;
    this.latestSnapshot = null;
    this.latestSnapshotSubmittedAt = 0;
    this.nextDrawAt = 0;
    if (this.frameRequest !== null) {
      this.cancelFrame(this.frameRequest);
      this.frameRequest = null;
    }
  }

  isRunning(): boolean {
    return this.running;
  }

  private schedule(): void {
    if (!this.running || this.frameRequest !== null) return;
    this.frameRequest = this.requestFrame((nowMs) => this.tick(nowMs));
  }

  private tick(nowMs: number): void {
    this.frameRequest = null;
    if (!this.running) return;
    if (this.latestSnapshot !== null && (this.nextDrawAt === 0 || nowMs >= this.nextDrawAt)) {
      const scheduledAt = this.nextDrawAt;
      const renderStartedAt = this.now();
      this.onRender(this.latestSnapshot, nowMs);
      const renderCompletedAt = this.now();
      this.onPerformanceSample?.({
        renderWorkMs: Math.max(0, renderCompletedAt - renderStartedAt),
        snapshotQueueMs: Math.max(0, renderCompletedAt - this.latestSnapshotSubmittedAt),
        missedDeadlines:
          scheduledAt === 0
            ? 0
            : Math.max(0, Math.floor((nowMs - scheduledAt) / this.frameIntervalMs)),
      });
      if (!this.running) return;
      if (this.nextDrawAt === 0) this.nextDrawAt = nowMs + this.frameIntervalMs;
      else {
        do {
          this.nextDrawAt += this.frameIntervalMs;
        } while (this.nextDrawAt <= nowMs);
      }
    }
    this.schedule();
  }
}
